/**
 * Unified image-generation state for async image-story panels.
 * Used by the narration scene list, reels, and progress dialog.
 */

export const IMAGE_STORY_PANEL_ACTIVE_STATUSES = new Set([
  'pending',
  'script_ready',
  'prompt_ready',
  'generating',
]);

export const IMAGE_STORY_PANEL_STATUS_LABELS = {
  pending: 'Waiting',
  script_ready: 'Script ready',
  prompt_ready: 'Waiting for style anchor',
  generating: 'Generating image…',
  completed: 'Image ready',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

function panelCountFromOptions(options) {
  return Number(options?.panelCount) || 0;
}

export function imageStoryPanelStatusLabel(status, panelNumber, panelCount = 0) {
  const normalized = String(status || 'pending').trim().toLowerCase();
  if (normalized === 'prompt_ready' && Number(panelNumber) > 1) {
    return 'Waiting for style anchor';
  }
  return IMAGE_STORY_PANEL_STATUS_LABELS[normalized] || normalized;
}

/**
 * @returns {import('./imageStoryPanelImageState').ImageStoryPanelImageState}
 */
export function normalizeImageStoryPanelImageState(source = {}, options = {}) {
  const panelNumber = Number(
    source?.panel_number ?? source?.segment_number ?? options?.panelNumber ?? 0,
  );
  const panelCount = panelCountFromOptions(options);
  const imageUrl =
    source?.image_url
    || source?.source_image_url
    || source?.panel_source_image_url
    || null;
  let status = String(source?.status ?? source?.image_generation_status ?? 'pending')
    .trim()
    .toLowerCase();
  const errorRaw = String(source?.error ?? source?.image_generation_error ?? '').trim() || null;
  const hasImage = Boolean(imageUrl);
  // Manual retry can set image_url without clearing a stale failed flag in older API responses.
  if (hasImage && (status === 'failed' || status === 'cancelled')) {
    status = 'completed';
  }
  const error = hasImage ? null : errorRaw;
  const isActive = IMAGE_STORY_PANEL_ACTIVE_STATUSES.has(status);
  const canRetry = !hasImage && !isActive && (
    status === 'failed'
    || status === 'cancelled'
    || !status
    || status !== 'completed'
  );
  const canRegenerate = hasImage && !isActive;
  const canCancel = ['pending', 'script_ready', 'prompt_ready', 'generating', 'failed'].includes(status);
  const isStyleAnchor = panelCount > 1 && panelNumber === 1;
  const retryLabel = status === 'failed' || status === 'cancelled' ? 'Retry image' : 'Generate image';
  const regenerateLabel = 'Regenerate image';
  const missingImageLabel = error ? 'Image failed' : 'No image';

  return {
    panelNumber,
    status,
    imageUrl,
    error,
    hasImage,
    isActive,
    canRetry,
    canRegenerate,
    canCancel,
    isStyleAnchor,
    statusLabel: imageStoryPanelStatusLabel(status, panelNumber, panelCount),
    retryLabel,
    regenerateLabel,
    missingImageLabel,
    imageStoryJobId: source?.image_story_job_id || options?.imageStoryJobId || null,
  };
}

export function imageStoryPanelImageStateFromScene(scene, options = {}) {
  const rawImageStatus = String(scene?.image_status || '').trim().toLowerCase();
  const mappedStatus = scene?.status
    ?? scene?.image_generation_status
    ?? (rawImageStatus === 'running' ? 'generating' : rawImageStatus || undefined);
  return normalizeImageStoryPanelImageState(
    {
      ...scene,
      status: mappedStatus,
      error: scene?.error ?? scene?.image_error,
    },
    options,
  );
}

export function imageStoryPanelImageStateFromJobPanel(panel, options = {}) {
  return normalizeImageStoryPanelImageState(
    {
      panel_number: panel?.panel_number,
      status: panel?.status,
      image_url: panel?.image_url,
      source_image_url: panel?.source_image_url,
      error: panel?.error,
      image_story_job_id: options?.imageStoryJobId,
    },
    options,
  );
}

export function buildImageStoryReelItemFromScene(scene, index, options = {}) {
  const panelImage = imageStoryPanelImageStateFromScene(scene, options);
  const resolveUrl = options.resolveImageUrl;
  const imageUrl = panelImage.hasImage
    ? (typeof resolveUrl === 'function' ? resolveUrl(scene) : panelImage.imageUrl)
    : null;

  return {
    id: scene?.client_id || `scene-${scene?.segment_number || index + 1}`,
    frameNumber: scene?.segment_number ?? index + 1,
    title: `Scene ${scene?.segment_number ?? index + 1}`,
    imageUrl,
    panelImage,
    canRetryImage: panelImage.canRetry,
    canRegenerateImage: panelImage.canRegenerate,
    status: panelImage.status,
    statusLabel: !panelImage.hasImage && panelImage.canRetry
      ? panelImage.missingImageLabel
      : panelImage.statusLabel,
    error: panelImage.error,
    scene,
    testId: `narration-scene-row-${scene?.segment_number ?? index + 1}`,
  };
}

export function buildImageStoryReelItemFromJobPanel(panel, index, options = {}) {
  const panelCount = panelCountFromOptions(options);
  const panelImage = imageStoryPanelImageStateFromJobPanel(panel, options);
  const panelNumber = panelImage.panelNumber || index + 1;
  const sections = [];

  if (panel?.narration) {
    sections.push({
      key: 'narration',
      label: 'Narration',
      content: panel.narration,
      defaultOpen: true,
    });
  }
  if (panel?.scene) {
    sections.push({
      key: 'scene',
      label: 'Scene',
      content: panel.scene,
    });
  }
  if (panel?.visual_prompt) {
    sections.push({
      key: 'visual_prompt',
      label: 'Visual prompt',
      content: panel.visual_prompt,
    });
  }
  if (!sections.length && panelImage.status === 'generating') {
    sections.push({
      key: 'status',
      label: 'Status',
      content: 'Generating image on provider queue…',
      defaultOpen: true,
    });
  }

  const jobTitle = options?.jobTitle || '';
  const title = panelImage.isStyleAnchor
    ? `Panel ${panelNumber} · Style anchor`
    : (jobTitle ? `Panel ${panelNumber} · ${jobTitle}` : `Panel ${panelNumber}`);

  return {
    id: `panel-${panelNumber}`,
    frameNumber: panelNumber,
    frameLabel: panelImage.isStyleAnchor ? `${String(panelNumber).padStart(2, '0')}A` : undefined,
    title,
    imageUrl: panelImage.hasImage ? panelImage.imageUrl : null,
    panelImage,
    canRetryImage: panelImage.canRetry,
    canRegenerateImage: panelImage.canRegenerate,
    status: panelImage.status,
    statusLabel: panelImage.statusLabel,
    sections,
    error: panelImage.error,
    panel,
    panelNumber,
    panelStatus: panelImage.status,
    testId: `image-story-panel-row-${panelNumber}`,
  };
}

/** @typedef {ReturnType<typeof normalizeImageStoryPanelImageState>} ImageStoryPanelImageState */
